<template>
  <div>
    <v-row
      no-gutters
      align="center"
      class="mb-1"
    >
      <v-col cols="auto">
        <div class="subtitle-1">
          Payments (Cash Flow) - {{ direction === 'inbound' ? 'Inbound' : 'Outbound' }}
        </div>
      </v-col>
      <v-col
        cols="auto"
        class="pl-4"
      >
        <v-btn
          rounded
          text
          class="caption text-none ma-0"
          color="grey darken-2"
          :loading="exportingToXlsx"
          @click="downloadXls()"
        >
          <v-icon
            left
            small
          >
            download
          </v-icon>
          Export
        </v-btn>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-btn
          v-if="$can('add-actual-payments', 'Yacht') || $can('add-forecast-payments', 'Yacht')"
          color="primary"
          rounded
          text
          class="ma-0"
          @click="initAddPayment()"
        >
          <v-icon
            left
            small
          >
            add
          </v-icon>
          Add Payment
        </v-btn>
      </v-col>
    </v-row>

    <v-card elevation="10">
      <v-card-text class="pa-0">
        <v-simple-table>
          <thead>
            <tr>
              <th />
              <th>Added by</th>
              <th>Category</th>
              <th>Date Due / Paid</th>
              <th>Status</th>
              <th>Paid</th>
              <th>Amount</th>
              <th v-if="$can('edit-payments', 'Yacht') || $can('delete-payments', 'Yacht')" />
            </tr>
          </thead>
          <tbody>
            <tr v-if="payments.length === 0">
              <td
                :colspan="$can('add-actual-payments', 'Yacht') || $can('add-forecast-payments', 'Yacht') ? 8 : 7"
                class="pa-16 text-center"
              >
                There are no payments yet.
                <a
                  href="#"
                  @click.stop.prevent="initAddPayment()"
                >Add payment</a>
              </td>
            </tr>
            <tr
              v-for="payment in payments"
              :key="payment.id"
              :class="{ 'orange lighten-5': payment.type === 'forecast', 'blue lighten-5': payment.type === 'actual' }"
            >
              <td style="width: 0">
                <v-chip
                  v-if="payment.type === 'forecast'"
                  label
                  color="orange darken-2"
                  small
                  outlined
                >
                  Forecast
                </v-chip>
                <v-chip
                  v-else-if="payment.type === 'actual'"
                  label
                  color="blue darken-2"
                  small
                  outlined
                >
                  Actual
                </v-chip>
              </td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      <span
                        v-if="payment.updated_by_user"
                      >
                        {{ payment.updated_by_user.staff.name }}
                      </span>
                      <span
                        v-else-if="payment.created_by_user"
                      >
                        {{ payment.created_by_user.staff.name }}
                      </span>
                      <span
                        v-else
                        class="grey--text"
                      >
                        system
                      </span>
                    </div>
                  </template>
                  <div>
                    <div>
                      Added by <strong>{{ payment.created_by_user ? payment.created_by_user.staff.name : 'system' }}</strong>
                      on <strong>{{ payment.created_at | moment('D MMM YYYY HH:mm:ss') }}</strong>
                    </div>
                    <span v-if="payment.created_at !== payment.updated_at">
                      Updated by <strong>{{ payment.updated_by_user ? payment.updated_by_user.staff.name : 'system' }}</strong>
                      on <strong>{{ payment.updated_at | moment('D MMM YYYY HH:mm:ss') }}</strong>
                    </span>
                  </div>
                </v-tooltip>
              </td>
              <td>
                <div><strong>{{ categoryData[payment.category].label }}</strong></div>
                <div v-if="payment.description">
                  {{ payment.description }}
                </div>
                <div v-if="payment.files && payment.files.length > 0">
                  <v-chip
                    v-for="file in payment.files"
                    :key="payment.id + file.id"
                    label
                    small
                    outlined
                    @click="openDocument(file.id)"
                  >
                    <v-icon
                      left
                      x-small
                    >
                      far fa-file
                    </v-icon>
                    <span class="blue--text">{{ file.file_name }}</span>
                  </v-chip>
                </div>
              </td>
              <td>
                {{ payment.date | moment('D MMM YYYY') }}
              </td>
              <td>
                <v-chip
                  :color="statusData[payment.status].color"
                  dark
                  label
                  small
                >
                  {{ statusData[payment.status].label }}
                </v-chip>
              </td>
              <td
                style="width: 0"
                class="text-right text-no-wrap"
              >
                <v-tooltip
                  v-if="payment.type === 'actual'"
                  :disabled="payment.original_currency === payment.currency"
                  top
                >
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <v-icon
                        v-if="payment.original_currency !== payment.currency"
                        x-small
                        left
                      >
                        fa fa-info-circle
                      </v-icon>
                      <span>{{ payment.original_currency || payment.currency }} {{ payment.original_amount | currency('') }}</span>
                    </span>
                  </template>
                  <span>
                    {{ payment.currency }} / {{ payment.original_currency }}: {{ payment.currency_exchange_rate_to_original }}
                  </span>
                </v-tooltip>
              </td>
              <td
                style="width: 0"
                class="text-right text-no-wrap"
              >
                <strong>{{ payment.currency }} {{ payment.amount | currency('') }}</strong>
              </td>
              <td
                v-if="$can('edit-payments', 'Yacht') || $can('delete-payments', 'Yacht')"
                style="width: 0"
                class="text-no-wrap"
              >
                <v-btn
                  v-if="$can('edit-payments', 'Yacht')"
                  icon
                  small
                  @click="initEditPayment(payment)"
                >
                  <v-icon x-small>
                    fa fa-pen
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="$can('delete-payments', 'Yacht')"
                  icon
                  small
                  @click="deletePaymentId = payment.id; deleteConfirmationDialogVisible = true"
                >
                  <v-icon x-small>
                    fa fa-trash
                  </v-icon>
                </v-btn>
              </td>
            </tr>
            <tr
              v-if="forecastAmount !== null"
            >
              <td colspan="5" />
              <td class="text-no-wrap">
                <strong>Total Forecast</strong>
              </td>
              <td class="text-right text-no-wrap">
                <strong>{{ forecastAmount | currency(yacht.currency) }}</strong>
              </td>
            </tr>
            <tr v-if="actualAmount !== null">
              <td colspan="5" />
              <td class="text-no-wrap">
                <strong>Total Actual</strong>
              </td>
              <td class="text-right text-no-wrap">
                <strong>{{ actualAmount | currency(yacht.currency) }}</strong>
              </td>
            </tr>
            <tr v-if="forecastAmount !== null && actualAmount !== null">
              <td colspan="5" />
              <td class="text-no-wrap">
                <strong>Total</strong>
              </td>
              <td class="text-right text-no-wrap">
                <strong>{{ forecastAmount + actualAmount | currency(yacht.currency) }}</strong>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <content-dialog
      :visible.sync="addPayment.dialog"
      max-width="500px"
      small-title
    >
      <template v-slot:dialogTitle>
        <span v-if="!addPayment.form.id">
          Add {{ direction === 'inbound' ? 'inbound' : 'outbound' }} cash flow
        </span>
        <span v-else>
          Edit {{ direction === 'inbound' ? 'inbound' : 'outbound' }} cash flow
        </span>
      </template>

      <v-card-text class="pa-4">
        <validation-observer ref="paymentValidation">
          <v-btn-toggle
            v-model="addPayment.form.type"
            class="v-btn-toggle--block elevation-1 mb-8 row no-gutters"
            color="white"
            active-class="primary"
            borderless
            mandatory
          >
            <v-btn
              v-if="$can('add-forecast-payments', 'Yacht')"
              large
              value="forecast"
              class="flex-grow-1"
            >
              Forecast
            </v-btn>
            <v-btn
              v-if="$can('add-actual-payments', 'Yacht')"
              large
              value="actual"
              class="flex-grow-1"
            >
              Actual
            </v-btn>
          </v-btn-toggle>

          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-autocomplete
              v-model="addPayment.form.category"
              outlined
              label="Category"
              placeholder="Select payment category"
              :items="categorySelectionData"
              item-text="label"
              item-value="key"
              clearable
              :error-messages="errors"
            />
          </validation-provider>

          <div v-if="selectedCategory">
            <v-row no-gutters>
              <v-col
                cols="10"
                class="mr-2"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{ required: true, max_value: selectedCategory.direction === paymentDirectionOutflow ? 0 : false }"
                >
                  <currency-input
                    v-model="addPayment.form.amount"
                    outlined
                    clearable
                    :label="addPayment.form.type === 'forecast' ? 'Expected payment amount' : 'Payment amount'"
                    :hint="selectedCategory && (selectedCategory.direction === paymentDirectionOutflow ? 'Has to be negative (outflow payment)' : (selectedCategory.direction === paymentDirectionBoth ? 'Can be positive (inflow) or negative (outflow)' : undefined))"
                    persistent-hint
                    :error-messages="errors"
                    @input="checkNegativeValue"
                  />
                </validation-provider>
              </v-col>
              <v-col>
                <v-select
                  v-model="addPayment.form.currency"
                  :items="['EUR', 'GBP', 'USD', 'SEK']"
                  :label="addPayment.form.type === 'forecast' ? 'Expected currency' : 'Currency'"
                  :disabled="addPayment.form.type === 'forecast'"
                  outlined
                />
              </v-col>
            </v-row>

            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-menu v-model="addPayment.datePickerVisible">
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="addPayment.form.date ? $moment(addPayment.form.date).format('D MMM YYYY') : null"
                    outlined
                    :label="addPayment.form.type === 'forecast' ? 'Expected date of payment' : 'Date of payment'"
                    placeholder="Select date"
                    clearable
                    readonly
                    :error-messages="errors"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="addPayment.form.date"
                  :max="addPayment.form.type === 'actual' ? $moment().format('YYYY-MM-DD') : undefined"
                  @change="addPayment.datePickerVisible = false"
                />
              </v-menu>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              :rules="{ 'required': selectedCategory && selectedCategory.description_required }"
            >
              <v-textarea
                v-model="addPayment.form.description"
                outlined
                placeholder="Add any notes or comments"
                label="Notes / comments"
                rows="5"
                :error-messages="errors"
              />
            </validation-provider>

            <div v-if="addPayment.form.type === 'actual'">
              <v-subheader class="mt-2">
                Upload documents
              </v-subheader>
              <dropzone
                v-if="addPayment.dialog"
                :category="fileCategoryDocument"
                :initial-files="addPayment.form.files || []"
                :dropzone-id="'payment-' + (addPayment.form.id || 'new')"
              />
            </div>
          </div>
        </validation-observer>
      </v-card-text>

      <template v-slot:dialogActions>
        <v-spacer />
        <v-btn
          text
          color="grey"
          @click="addPayment.dialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :loading="false"
          @click="submitPayment"
        >
          <v-icon
            small
            left
          >
            check
          </v-icon>
          Save
        </v-btn>
      </template>
    </content-dialog>

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this payment?"
      @confirmed="deletePayment()"
    />
  </div>
</template>

<script>
import ContentDialog from '@/components/helpers/Dialog.vue'
import cloneDeep from 'lodash/cloneDeep'
import YachtPayment from '@/models/YachtPayment'
import CurrencyInput from '@/components/helpers/CurrencyInput.vue'
import Yacht from '@/models/Yacht'
import Dropzone from '@/components/helpers/Dropzone.vue'
import File from '@/models/File'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog.vue'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'

const emptyPaymentForm = {
  id: null,
  category: null,
  type: null,
  amount: null,
  currency: null,
  date: null,
  description: ''
}

export default {
  name: 'FinanceDetailsCashFlow',
  components: { ConfirmDeleteDialog, Dropzone, CurrencyInput, ContentDialog },
  mixins: [ deleteConfirmationMixin ],
  props: {
    yacht: {
      type: Object,
      required: true
    },
    direction: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      addPayment: {
        dialog: false,
        form: cloneDeep(emptyPaymentForm),
        datePickerVisible: false,
        submitting: false
      },
      deletePaymentId: null,
      exportingToXlsx: false
    }
  },
  computed: {
    selectedCategory() {
      if (!this.addPayment.form.category) return undefined

      return this.categoryData[this.addPayment.form.category]
    },
    payments() {
      if (!this.yacht.yacht_payments) return []

      return this.yacht.yacht_payments.filter(p => p.direction === this.direction)
    },
    forecastAmount() {
      if (this.payments.filter(p => p.type === 'forecast').length === 0) return null

      return this.payments.filter(p => p.type === 'forecast').map(p => p.amount).reduce((a, b) => a + b)
    },
    actualAmount() {
      if (this.payments.filter(p => p.type === 'actual').length === 0) return null

      return this.payments.filter(p => p.type === 'actual').map(p => p.amount).reduce((a, b) => a + b)
    }
  },
  watch: {
    'addPayment.form.type': function () {
      if (this.addPayment.form.type === 'actual' && !this.addPayment.form.id) {
        this.addPayment.form.date = this.$moment().format('YYYY-MM-DD')
      } else if (!this.addPayment.form.id) {
        this.addPayment.form.date = null
      }
    }
  },
  created() {
    this.categoryData = YachtPayment.categoryData
    this.categorySelectionData = Object.values(YachtPayment.categoryData)
    this.statusData = YachtPayment.statusData
    this.paymentDirectionInflow = YachtPayment.DIRECTION_INFLOW
    this.paymentDirectionOutflow = YachtPayment.DIRECTION_OUTFLOW
    this.paymentDirectionBoth = YachtPayment.DIRECTION_BOTH
    this.fileCategoryDocument = File.CATEGORY_DOCUMENT
  },
  methods: {
    checkNegativeValue() {
      if (!this.selectedCategory) return

      if (this.selectedCategory.direction === this.paymentDirectionOutflow && this.addPayment.form.amount > 0) this.addPayment.form.amount = -this.addPayment.form.amount
      if (this.selectedCategory.direction === this.paymentDirectionInflow && this.addPayment.form.amount < 0) this.addPayment.form.amount = -this.addPayment.form.amount
    },
    async submitPayment() {
      if (!(await this.$refs.paymentValidation.validate())) return

      try {
        this.addPayment.submitting = true

        // Get the current payment's dropzoneId
        const dropzoneId = 'payment-' + (this.addPayment.form.id || 'new')

        // Filter files by the current payment's dropzoneId
        const paymentFiles = this.$store.state.dropzone.files.filter(file => file.dropzoneId === dropzoneId)

        if (this.addPayment.form.id) {
          await Yacht.api.editYachtPayment(this.yacht.id, this.addPayment.form.id, {
            ...this.addPayment.form,
            files: {
              data: paymentFiles.map(file => { return file.response }),
              removedFileIds: this.$store.state.dropzone.removedIds
            }
          })

          this.$store.dispatch('alert', {
            'text': 'Payment successfully edited',
            'color': 'success'
          })
        } else {
          await Yacht.api.addYachtPayment(this.direction, this.yacht.id, {
            ...this.addPayment.form,
            files: {
              data: paymentFiles.map(file => { return file.response }),
              removedFileIds: this.$store.state.dropzone.removedIds
            }
          })

          this.$store.dispatch('alert', {
            'text': 'Payment successfully added',
            'color': 'success'
          })
        }

        this.addPayment.dialog = false

        this.$emit('update:yacht')
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'There was an error',
          'color': 'error'
        })
        throw e
      } finally {
        this.addPayment.submitting = false
      }
    },
    async deletePayment() {
      try {
        this.deleting = true

        await Yacht.api.deleteYachtPayment(this.yacht.id, this.deletePaymentId)

        this.$store.dispatch('alert', {
          'text': 'Payment successfully deleted',
          'color': 'success'
        })

        this.$emit('update:yacht')
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'There was an error',
          'color': 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    initAddPayment() {
      this.addPayment.dialog = true

      this.addPayment.form = cloneDeep(emptyPaymentForm)

      this.addPayment.form.currency = this.yacht.currency

      if (this.$can('add-actual-payments', 'Yacht')) this.addPayment.form.type = 'actual'
      else if (this.$can('add-forecast-payments', 'Yacht')) this.addPayment.form.type = 'forecast'

      this.$store.dispatch('clearFiles')
    },
    initEditPayment(payment) {
      this.addPayment.dialog = true

      // Clear any existing files in the store
      this.$store.dispatch('clearFiles')

      this.addPayment.form = {
        id: payment.id,
        category: payment.category,
        type: payment.type,
        amount: payment.amount,
        currency: payment.currency,
        date: this.$moment(payment.date).format('YYYY-MM-DD'),
        description: payment.description,
        files: payment.files || []
      }
    },
    openDocument(fileId) {
      File.api.openDocument(fileId)
    },
    async downloadXls() {
      this.exportingToXlsx = true

      try {
        let result = await Yacht.api.exportPayments(this.yacht.id, this.direction)

        window.open(result.path, '_blank')
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong',
          'color': 'error'
        })
        throw e
      } finally {
        this.exportingToXlsx = false
      }
    }

  }
}
</script>
